import { useState } from "react";
import dayjs from "dayjs";
import { addComma, getImageUrlWithMaxWidth, HOSTNAME, isExternalUrl } from "@src/utils";
import BottomFixedModal from "@src/components/common/bottomFixedModal/BottomFixedModal";
import toast from "@src/utils/toast";
import { addLikeSlot, removeLikeSlot } from "@src/api/weddingHall/weddingHall";
import { TSlot } from "@src/types/weddingHall";
import { ReactComponent as IMeal } from "@assets/images/icon/weddinghall/meal.svg";
import { ReactComponent as ICalendar } from "@assets/images/icon/weddinghall/calendar.svg";
import { ReactComponent as ITime } from "@assets/images/icon/weddinghall/time.svg";
import { ReactComponent as IUser } from "@assets/images/icon/weddinghall/user.svg";
import "@src/components/weddingHall/product/productHallCard.scss";

interface Props {
  slotInfo: TSlot;
  slotLike?: boolean;
  onClickHeart?: (e: React.MouseEvent) => void;
  useLikeToast?: boolean;
}

const SlotCard = (props: Props) => {
  const slotInfo = props.slotInfo;
  const { weddingDayId, isLike } = slotInfo;
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [like, setLike] = useState<boolean>(isLike);

  const weddingDate = dayjs(slotInfo.weddingDate?.replaceAll(".", "-")).format("YY.MM.DD(ddd)");
  const weddingTimes = slotInfo.weddingTime.split(":");
  const hasSlotModal =
    typeof slotInfo.rentalPrice === "number" && typeof slotInfo.flowerPrice === "number";

  const openDetailPrice = (e) => {
    if (hasSlotModal) {
      e.stopPropagation();
      setShowPriceModal(true);
    }
  };

  //하트 클릭 커스텀 함수를 넘겨주지 않으면 기본적으로 동작
  const onClickHeartDefault = (e) => {
    e.stopPropagation();
    setLike(!like);

    if (like === false) {
      addLikeSlot(weddingDayId).then((res) => {
        if (res === 200 && props.useLikeToast) {
          toast.showSnackbar("위시리스트에 추가했습니다.", {
            buttonText: "위시리스트 가기",
            onClick: () => {
              window.location.href = isExternalUrl(`${HOSTNAME}/my-interest/like/slot?tab=slot`);
            },
          });
        }
      });
    } else {
      removeLikeSlot(weddingDayId).then((res) => {
        if (res === 200 && props.useLikeToast) {
          toast.showSnackbar("위시리스트에서 삭제했습니다.", { isShowClose: true });
        }
      });
    }
  };

  const moveToPricePage = () => {
    window.location.href = isExternalUrl(
      `${HOSTNAME}/weddinghall/${slotInfo.partnerUuid}/slot?id=${slotInfo.weddingDayId}`,
    );
  };

  return (
    <div className={`product-card-wrap card-${slotInfo.weddingDayId} slot-card-wrap `}>
      <div className="product-wrap card-item" onClick={moveToPricePage}>
        <div className="thumb thumb-square-80">
          <img
            className="product-img"
            src={getImageUrlWithMaxWidth(slotInfo.imageUrl) || ""}
            alt={slotInfo.partnerProfileName}
            onLoad={(e) => {
              const target = e.target as HTMLImageElement;
              if (slotInfo?.imageUrl) {
                target.style.opacity = "1";
              }
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.opacity = "0";
            }}
            loading="lazy"
          />
        </div>
        <div className="hall-info-wrap slot-info-wrap">
          <p className="adress">{`${slotInfo.region} ${slotInfo.subRegion}`}</p>
          <h2 className="slot-hallname">
            <span>{slotInfo.partnerProfileName}</span>
            <span>{slotInfo.hallName}</span>
          </h2>
          <p className="bold slot-price">
            {slotInfo.discountRate > 0 && (
              <span className="discount">{slotInfo.discountRate}%</span>
            )}
            <span className={`price ${hasSlotModal ? "underline" : ""}`} onClick={openDetailPrice}>
              {addComma(slotInfo.finalPrice)}원
            </span>
            <span className="meal-price">
              {`(`}
              <IMeal className="slot-card-icon" />
              {` ${addComma(slotInfo.mealPrice)}원)`}
            </span>
          </p>
          <p className={`time-contents slot-time`}>
            <span>
              <ICalendar className="slot-card-icon" />
              {`${weddingDate}`}
            </span>
            <span>
              <ITime className="slot-card-icon" />
              {`${weddingTimes[0]}:${weddingTimes[1]}`}
            </span>
            <span>
              <IUser className="slot-card-icon" />
              {slotInfo.guarantee}명
            </span>
          </p>
        </div>
        <i
          className={`icon ${props?.onClickHeart ? (isLike ? "icon-heart-filled" : "icon-heart-gray900") : like ? "icon-heart-filled" : "icon-heart-gray900"}`}
          onClick={props.onClickHeart ? props.onClickHeart : onClickHeartDefault}
        />
      </div>

      <BottomFixedModal
        className="slot-detail-price-modal"
        isTransition={true}
        isShow={showPriceModal}
        hideModal={() => setShowPriceModal(false)}
        title={slotInfo.partnerProfileName}
      >
        <ul>
          <li>
            <span>대관료</span>
            <span>{`${addComma(slotInfo.rentalPrice)}원`}</span>
          </li>
          <li>
            <span>플라워</span>
            <span>
              {slotInfo.flowerPrice === 0 ? "포함" : `${addComma(slotInfo.flowerPrice)}원`}
            </span>
          </li>
          <li>
            <span>1인 식대</span>
            <span>{`${addComma(slotInfo.mealPrice)}원`}</span>
          </li>
          <li>
            <span>1인 식대 X {slotInfo.guarantee}명</span>
            <span>{`${addComma(slotInfo.mealPrice * slotInfo.guarantee)}원`}</span>
          </li>
        </ul>
        <div className="btn-wrap" style={{ padding: "20px 0" }}>
          <button
            className="btn btn-full btn-giant btn-red500"
            onClick={() => {
              moveToPricePage();
              setShowPriceModal(false);
            }}
          >
            {" "}
            더 자세히 보기
          </button>
        </div>
      </BottomFixedModal>
    </div>
  );
};

export default SlotCard;
